import { useState, useEffect } from 'react';
import { ArrowLeft, Award, Calendar, Clock, X, ChevronDown, Check } from 'lucide-react';
import { authAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { getAvatarUrl } from '../../utils/avatarUtils';

export default function ExpertAccountSettings() {
  const { uploadAvatar } = useAuth();
  // Profile data state
  const [profileData, setProfileData] = useState(null);
  const [userData, setUserData] = useState({
    first_name: '',
    last_name: '',
    specialization: '',
    email: '',
    phone: '',
    areas_of_expertise: [],
    biography: '',
    years_of_experience: 0,
    booking_fee: 0,
    languages: [],
    credentials: '',
    available_days: [],
    available_time: ''
  });

  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorAlert, setShowErrorAlert] = useState(false);

  // Multi-select states
  const [showLanguagesDropdown, setShowLanguagesDropdown] = useState(false);
  const [showExpertiseDropdown, setShowExpertiseDropdown] = useState(false);

  // Fetch user profile on component mount
  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setIsLoading(true);
      const response = await authAPI.getUserProfile();
      
      if (response.success) {
        const avatarUrl = response.data?.avatar || response.data?.avatar_url;
        const fullName = response.data?.full_name || `${response.data?.first_name || ''} ${response.data?.last_name || ''}`.trim();
        setProfileData({
          ...response.data,
          avatar: avatarUrl,
          avatarUrl: getAvatarUrl(
            avatarUrl,
            fullName,
            response.data?.first_name || '',
            response.data?.last_name || '',
            200
          )
        });
        // Map profile data to userData structure
        // Try to get expert-specific data from profile response
        const expertData = response.data.expert || response.data;
        setUserData(prevData => ({
          ...prevData,
          first_name: response.data.first_name || '',
          last_name: response.data.last_name || '',
          email: response.data.email || '',
          phone: response.data.phone || '',
          biography: response.data.bio || expertData.biography || '',
          // Expert-specific fields from profile or expert data
          specialization: expertData.specialization || prevData.specialization,
          areas_of_expertise: expertData.areas_of_expertise || response.data.areas_of_expertise || prevData.areas_of_expertise,
          years_of_experience: expertData.years_of_experience || response.data.years_of_experience || prevData.years_of_experience,
          booking_fee: expertData.booking_fee || response.data.booking_fee || prevData.booking_fee,
          languages: expertData.languages || response.data.languages || prevData.languages,
          credentials: expertData.credentials || response.data.credentials || prevData.credentials,
          available_days: expertData.available_days || response.data.available_days || prevData.available_days,
          available_time: expertData.available_time || response.data.available_time || prevData.available_time
        }));
      } else {
        throw new Error(response.message || 'Failed to fetch profile');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      showErrorAlertMessage('Failed to load profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showErrorAlertMessage('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showErrorAlertMessage('Image size should be less than 5MB');
      return;
    }

    try {
      setIsLoading(true);
      const result = await uploadAvatar(file);
      if (result.success) {
        showSuccessAlertMessage('Profile picture updated successfully');
        // Reload profile to get updated avatar URL
        await fetchUserProfile();
        // Force page reload to update avatar in topnav
        window.location.reload();
      } else {
        showErrorAlertMessage(result.message || 'Failed to upload avatar');
      }
    } catch (err) {
      console.error('Error uploading avatar:', err);
      showErrorAlertMessage('Failed to upload avatar. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData({
      ...userData,
      [name]: value
    });
  };

  // Handle multi-select changes
  const handleMultiSelectChange = (field, value) => {
    setUserData({
      ...userData,
      [field]: value
    });
  };

  // Modern multi-select functions
  const toggleLanguage = (language) => {
    const updatedLanguages = userData.languages.includes(language)
      ? userData.languages.filter(lang => lang !== language)
      : [...userData.languages, language];
    handleMultiSelectChange('languages', updatedLanguages);
  };

  const toggleExpertise = (expertise) => {
    const updatedExpertise = userData.areas_of_expertise.includes(expertise)
      ? userData.areas_of_expertise.filter(exp => exp !== expertise)
      : [...userData.areas_of_expertise, expertise];
    handleMultiSelectChange('areas_of_expertise', updatedExpertise);
  };

  const removeLanguage = (language) => {
    const updatedLanguages = userData.languages.filter(lang => lang !== language);
    handleMultiSelectChange('languages', updatedLanguages);
  };

  const removeExpertise = (expertise) => {
    const updatedExpertise = userData.areas_of_expertise.filter(exp => exp !== expertise);
    handleMultiSelectChange('areas_of_expertise', updatedExpertise);
  };

  // API service functions
  const updateExpertDetails = async (data) => {
    try {
      const response = await authAPI.updateExpertProfile(data);

      // Check if the response indicates failure
      if (!response.success) {
        throw new Error(JSON.stringify(response));
      }

      return response;
    } catch (error) {
      console.error('Error updating expert details:', error);
      throw error;
    }
  };

  // Save all expert details
  const saveAllChanges = async () => {
    setIsSaving(true);
    try {
      const updateData = {
        ...(userData.first_name && { first_name: userData.first_name }),
        ...(userData.last_name && { last_name: userData.last_name }),
        ...(userData.phone && { phone: userData.phone }),
        ...(userData.biography && { biography: userData.biography }),
        ...(userData.specialization && { specialization: userData.specialization }),
        ...(userData.areas_of_expertise?.length && { areas_of_expertise: userData.areas_of_expertise }),
        ...(userData.years_of_experience && { years_of_experience: parseInt(userData.years_of_experience) }),
        ...(userData.booking_fee && { booking_fee: parseFloat(userData.booking_fee) }),
        ...(userData.languages?.length && { languages: userData.languages }),
        ...(userData.credentials && { credentials: userData.credentials }),
        ...(userData.available_days?.length && { available_days: userData.available_days }),
        ...(userData.available_time && { available_time: userData.available_time }),
      };

      await updateExpertDetails(updateData);
      showSuccessAlertMessage('Expert details updated successfully!');
    } catch (error) {
      console.error('Error saving changes:', error);
      try {
        const errorResponse = JSON.parse(error.message);
        if (errorResponse.errors) {
          const errorMessages = [];
          Object.keys(errorResponse.errors).forEach(field => {
            errorResponse.errors[field].forEach(errorMsg => {
              errorMessages.push(`${field.replace('_', ' ')}: ${errorMsg}`);
            });
          });
          showErrorAlertMessage(errorMessages.join(', '));
        } else {
          showErrorAlertMessage(errorResponse.message || 'Failed to save changes. Please try again.');
        }
      } catch (parseError) {
        showErrorAlertMessage('Failed to save changes. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Alert functions
  const showSuccessAlertMessage = (message) => {
    setSuccessMessage(message);
    setShowSuccessAlert(true);
    setTimeout(() => {
      setShowSuccessAlert(false);
      setSuccessMessage('');
    }, 4000);
  };

  const showErrorAlertMessage = (message) => {
    setErrorMessage(message);
    setShowErrorAlert(true);
    setTimeout(() => {
      setShowErrorAlert(false);
      setErrorMessage('');
    }, 4000);
  };

  // Days of the week for availability
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const availableLanguages = ['English', 'French', 'Spanish', 'Portuguese', 'Arabic', 'Hausa', 'Yoruba', 'Igbo'];
  const availableExpertise = ['Crop Science', 'Soil Health', 'Pest Management', 'Sustainable Agriculture', 'Livestock Management', 'Aquaculture', 'Agricultural Economics', 'Food Security'];

  // Modern Multi-Select Component
  const ModernMultiSelect = ({ label, selectedItems, options, onToggle, onRemove, isOpen, onToggleDropdown, placeholder }) => (
      <div className="account-field-apss">
        <div className="field-label-apss">{label}</div>
      <div className="modern-multiselect-apss">
        {/* Selected items display */}
        <div className="selected-items-apss">
          {selectedItems.map((item, index) => (
            <div key={index} className="selected-item-apss">
              <span>{item}</span>
              <button 
                type="button"
                className="remove-item-apss"
                onClick={() => onRemove(item)}
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
        
        {/* Dropdown trigger */}
        <div 
          className="multiselect-trigger-apss"
          onClick={onToggleDropdown}
        >
          <span className="placeholder-apss">
            {selectedItems.length > 0 ? `Add more...` : placeholder}
          </span>
          <ChevronDown size={16} className={`chevron-apss ${isOpen ? 'open' : ''}`} />
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div className="multiselect-dropdown-apss">
            {options.map((option, index) => (
              <div
                key={index}
                className={`dropdown-item-apss ${selectedItems.includes(option) ? 'selected' : ''}`}
                onClick={() => onToggle(option)}
              >
                <span>{option}</span>
                {selectedItems.includes(option) && (
                  <Check size={16} className="check-icon-apss" />
                )}
              </div>
            ))}
          </div>
          )}
        </div>
      </div>
    );
    
  if (isLoading) {
    return (
      <div className="account-settings-container-apss">
        <div className="loading-container-apss">
          <div className="loading-spinner-apss"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="account-settings-container-apss">
      {/* Success Alert */}
      {showSuccessAlert && (
        <div className="success-alert-right-apss">
          <div className="success-alert-content-apss">
            <div className="success-alert-icon-apss">✓</div>
            <div className="success-alert-message-apss">{successMessage}</div>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {showErrorAlert && (
        <div className="error-alert-right-apss">
          <div className="error-alert-content-apss">
            <div className="error-alert-icon-apss">!</div>
            <div className="error-alert-message-apss">{errorMessage}</div>
          </div>
        </div>
      )}
      
      <div className="account-settings-header-apss">
        <button className="back-button-apss" onClick={() => window.history.back()}>
          <ArrowLeft size={20} />
          <span>Back to Settings</span>
        </button>
        <h1 className="account-settings-title-apss">Expert Account Settings</h1>
      </div>

      {/* Profile Section */}
      <div className="profile-section-apss">
        <div className="profile-picture-container-apss">
          <img 
            src={profileData?.avatarUrl || getAvatarUrl(
              profileData?.avatar,
              profileData?.full_name || `${userData.first_name} ${userData.last_name}`.trim(),
              userData.first_name || '',
              userData.last_name || '',
              200
            )} 
            alt="Profile" 
            className="profile-picture-apss" 
          />
          <label htmlFor="expert-avatar-upload" className="change-picture-button-apss" style={{ cursor: 'pointer' }}>
            Change Photo
          </label>
          <input
            type="file"
            id="expert-avatar-upload"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleAvatarUpload}
            disabled={isLoading}
          />
        </div>
        <div className="profile-name-role-apss">
          <h2 className="profile-name-apss">{profileData?.full_name || `${userData.first_name} ${userData.last_name}`}</h2>
          <div className="profile-role-apss">{profileData?.role || 'Expert'}</div>
          <div className="profile-specialization-apss">{userData.specialization || 'Agricultural Expert'}</div>
          <div className="profile-experience-apss">{userData.years_of_experience} years of experience</div>
        </div>
      </div>

      {/* Personal Information Section */}
      <div className="settings-section-apss">
        <h3 className="section-title-apss">Personal Information</h3>
        <div className="section-content-apss">
          <div className="account-field-apss">
            <div className="field-label-apss">First Name</div>
            <input
              type="text"
              name="first_name"
              value={userData.first_name}
              onChange={handleInputChange}
              className="field-input-apss"
            />
          </div>

          <div className="account-field-apss">
            <div className="field-label-apss">Last Name</div>
            <input
              type="text"
              name="last_name"
              value={userData.last_name}
              onChange={handleInputChange}
              className="field-input-apss"
            />
          </div>

          <div className="account-field-apss">
            <div className="field-label-apss">Email Address</div>
            <input
              type="email"
              name="email"
              value={userData.email}
              onChange={handleInputChange}
              className="field-input-apss"
            />
          </div>

          <div className="account-field-apss">
            <div className="field-label-apss">Phone Number</div>
            <input
              type="tel"
              name="phone"
              value={userData.phone}
              onChange={handleInputChange}
              className="field-input-apss"
            />
          </div>

          <div className="account-field-apss bio-field-apss">
            <div className="field-label-apss">Biography</div>
            <textarea
              name="biography"
              value={userData.biography}
              onChange={handleInputChange}
              className="field-textarea-apss"
              rows={4}
              placeholder="Tell us about your agricultural expertise and experience..."
            />
          </div>

          <ModernMultiSelect
            label="Languages"
            selectedItems={userData.languages}
            options={availableLanguages}
            onToggle={toggleLanguage}
            onRemove={removeLanguage}
            isOpen={showLanguagesDropdown}
            onToggleDropdown={() => setShowLanguagesDropdown(!showLanguagesDropdown)}
            placeholder="Select languages you speak"
          />
        </div>
      </div>

      {/* Professional Information Section */}
      <div className="settings-section-apss">
        <h3 className="section-title-apss">
          <Award size={18} className="section-icon-apss" />
          <span>Professional Information</span>
        </h3>
        <div className="section-content-apss">
          <div className="account-field-apss">
            <div className="field-label-apss">Specialization</div>
            <input
              type="text"
              name="specialization"
              value={userData.specialization}
              onChange={handleInputChange}
              className="field-input-apss"
            />
          </div>

          <div className="account-field-apss">
            <div className="field-label-apss">Years of Experience</div>
            <input
              type="number"
              name="years_of_experience"
              value={userData.years_of_experience}
              onChange={handleInputChange}
              className="field-input-apss"
              min="0"
            />
          </div>

          <ModernMultiSelect
            label="Areas of Expertise"
            selectedItems={userData.areas_of_expertise}
            options={availableExpertise}
            onToggle={toggleExpertise}
            onRemove={removeExpertise}
            isOpen={showExpertiseDropdown}
            onToggleDropdown={() => setShowExpertiseDropdown(!showExpertiseDropdown)}
            placeholder="Select your areas of expertise"
          />

          <div className="account-field-apss">
            <div className="field-label-apss">Credentials & Certifications</div>
            <textarea
              name="credentials"
              value={userData.credentials}
              onChange={handleInputChange}
              className="field-textarea-apss"
              rows={3}
              placeholder="List your certifications and credentials..."
            />
          </div>

          <div className="account-field-apss">
            <div className="field-label-apss">Booking Fee (₦/hour)</div>
            <input
              type="number"
              name="booking_fee"
              value={userData.booking_fee}
              onChange={handleInputChange}
              className="field-input-apss"
              min="0"
            />
          </div>
        </div>
      </div>

      {/* Availability Section */}
      <div className="settings-section-apss">
        <h3 className="section-title-apss">
          <Calendar size={18} className="section-icon-apss" />
          <span>Availability Settings</span>
        </h3>
        <div className="section-content-apss">
          <div className="account-field-apss">
            <div className="field-label-apss">Available Days</div>
            <div className="days-selection-apss">
              {daysOfWeek.map(day => (
                <button
                  key={day}
                  type="button"
                  className={`day-button-apss ${userData.available_days.includes(day) ? 'selected' : ''}`}
                  onClick={() => {
                    const updatedDays = userData.available_days.includes(day)
                      ? userData.available_days.filter(d => d !== day)
                      : [...userData.available_days, day];
                    handleMultiSelectChange('available_days', updatedDays);
                  }}
                >
                  {day.substring(0, 3)}
                </button>
              ))}
            </div>
          </div>

          <div className="account-field-apss">
            <div className="field-label-apss">Available Time</div>
            <input
              type="text"
              name="available_time"
              value={userData.available_time}
              onChange={handleInputChange}
              className="field-input-apss"
              placeholder="e.g., 09:00-17:00"
            />
          </div>
        </div>
      </div>


      {/* Save All Changes Button */}
      <div className="save-changes-container-apss">
        <button
          className="save-all-changes-button-apss"
          onClick={saveAllChanges}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save All Changes'}
        </button>
      </div>
    </div>
  );
}